import { defineStore } from 'pinia';
import {
    IDLE, LOADING, SUCCESS, ERROR,
} from '../../constants/loadingStatuses.constants';
import * as api from './api/features.api';
import { useShareUrlsStore } from '../shareUrls';
import { ref } from 'vue';
import { transformCompatibility, transformCompatibilityRollup } from '../../utils/features.util';

export const useFeaturesStore = defineStore('features', () => {
    const loadCompatibilityStatus = ref(IDLE);
    const resourceCompatibility = ref({});

    function loadCompatibilityStart() {
        loadCompatibilityStatus.value = LOADING;
    }

    function loadCompatibilitySuccess() {
        loadCompatibilityStatus.value = SUCCESS;
    }

    function loadCompatibilityError() {
        loadCompatibilityStatus.value = ERROR;
    }

    async function getFeatureCompatibility({ sharedResourceId, includeTopLevel = true }) {
        try {
            loadCompatibilityStart();

            const shareUrlsStore = useShareUrlsStore();

            const { editionFeatures, topLevelResources } = await api.getFeatureCompatibility({ sharedResourceId, includeTopLevel }, shareUrlsStore.packagingServiceUrl);

            resourceCompatibility.value = {
                sharedResourceId,
                compatibility: transformCompatibility(editionFeatures),
                dependencies: topLevelResources?.map(({ sharedResourceId: resourceId, editionFeatures: dependencyEditionFeatures }) => ({
                    sharedResourceId: resourceId,
                    compatibility: transformCompatibility(dependencyEditionFeatures),
                })),
            };

            loadCompatibilitySuccess();
        } catch (error) {
            loadCompatibilityError();
        }
    }

    async function getFeatureCompatibilityList({ sharedResourceIds }) {
        try {
            loadCompatibilityStart();

            const shareUrlsStore = useShareUrlsStore();

            const promises = sharedResourceIds.map((sharedResourceId) => api.getFeatureCompatibility({ sharedResourceId, includeTopLevel: false }, shareUrlsStore.packagingServiceUrl));
            const results = await Promise.allSettled(promises);

            const dependencies = results.map(({ value }) => {
                return {
                    sharedResourceId: value.sharedResourceId,
                    compatibility: transformCompatibility(value.editionFeatures),
                };
            });

            const response = {
                compatibility: {
                    resourceEditions: transformCompatibilityRollup(dependencies.map(({ compatibility }) => compatibility.resourceEditions)),
                    featureEditions: null,
                },
                dependencies,
            };

            resourceCompatibility.value = response;

            loadCompatibilitySuccess();
        } catch (error) {
            loadCompatibilityError();
        }
    }

    async function getFeatureCompatibilityFromLink({ linkId, includeTopLevel = true }) {
        try {
            loadCompatibilityStart();

            const shareUrlsStore = useShareUrlsStore();

            const { sharedResourceId, editionFeatures, topLevelResources } = await api.getFeatureCompatibilityFromLink({ linkId, includeTopLevel }, shareUrlsStore.packagingServiceUrl);

            resourceCompatibility.value = {
                sharedResourceId,
                compatibility: transformCompatibility(editionFeatures),
                dependencies: topLevelResources?.map(({ sharedResourceId: resourceId, editionFeatures: dependencyEditionFeatures }) => ({
                    sharedResourceId: resourceId,
                    compatibility: transformCompatibility(dependencyEditionFeatures),
                })),
            };

            loadCompatibilitySuccess();
        } catch (error) {
            loadCompatibilityError();
        }
    }

    return {
        loadCompatibilityStatus,
        resourceCompatibility,

        getFeatureCompatibility,
        getFeatureCompatibilityList,
        getFeatureCompatibilityFromLink,

        loadCompatibilityStart,
        loadCompatibilitySuccess,
        loadCompatibilityError,
    };
});
